import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Pagination,
} from "react-bootstrap";
import Card from "../../../components/Card";
import "./stylw.css";
import {
  getLabel,
  addLabel,
  updateLabel,
  deleteLabel,
  getLabelById,
} from "../../../services/service";
import { toast } from "react-toastify";

const LabelManager = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [modal, setModal] = useState({
    show: false,
    editing: false,
    id: null,
    labelKey: "",
    labelEn: "",
    labelHi: "",
  });

  // 🔹 Fetch labels from API (with pagination & optional search)
  const fetchLabels = async (page = 1, search = "") => {
    setError("");
    setLoading(true);
    setLabels([]); // Clear old data to prevent flicker of old page

    const startTime = Date.now();

    try {
      const params = { page, limit: 10 };
      if (search.trim()) params.search = search.trim();

      const res = await getLabel(params);

      setLabels(res.data.data || []);
      setPagination(
        res.data.pagination || {
          page: 1,
          totalPages: 1,
          totalItems: 0,
        }
      );
    } catch (err) {
      setError("Failed to fetch labels: " + err.message);
    } finally {
      const elapsed = Date.now() - startTime;
      const minLoaderTime = 500;
      const remaining = Math.max(0, minLoaderTime - elapsed);
      setTimeout(() => setLoading(false), remaining);
    }
  };

  // 🔹 Debounced search & pagination watcher
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchLabels(pagination.page, searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [pagination.page, searchTerm]);

  // 🔹 Handle Add / Update
  const handleSave = async () => {
    if (!modal.labelKey || !modal.labelEn || !modal.labelHi) {
      setError("All fields are required!");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
        key: modal.labelKey,
        value: { en: modal.labelEn, hi: modal.labelHi },
      };

      if (modal.editing && modal.id) {
        await updateLabel(modal.id, payload);
        toast.success("Label updated successfully!");
      } else {
        await addLabel(payload);
        toast.success("Label added successfully!");
      }

      await fetchLabels(1, searchTerm);
      setModal({ ...modal, show: false });
    } catch (err) {
      setError("Failed to save label: " + err.message);
      toast.error("Failed to save label: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Handle Edit
  const handleEdit = async (id) => {
    try {
      setLoading(true);
      const res = await getLabelById(id);
      const item = res.data.data || res.data;

      setModal({
        show: true,
        editing: true,
        id: item._id,
        labelKey: item.key,
        labelEn: item.value?.en || "",
        labelHi: item.value?.hi || "",
      });
      // toast.success("Label updated successfully!");
    } catch (err) {
      setError("Failed to fetch label details: " + err.message);
      // toast.error("Failed to fetch label details: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Handle Delete
  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      setLoading(true);
      await deleteLabel(deleteId);
      await fetchLabels(pagination.page, searchTerm);
      setShowDeleteModal(false);
      toast.success("Label deleted successfully!");
    } catch (err) {
      setError("Failed to delete label: " + err.message);
      toast.error("Failed to delete label: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Handle page change (only updates state, no API call directly)
  const handlePageChange = (newPage) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
    setLoading(true);
  };

  // 🔹 Pagination component
  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    return (
      <Pagination className="justify-content-center mt-3 flex-wrap">
        <Pagination.First
          onClick={() => handlePageChange(1)}
          disabled={pagination.page === 1}
        />
        <Pagination.Prev
          onClick={() => handlePageChange(Math.max(pagination.page - 1, 1))}
          disabled={pagination.page === 1}
        />

        {/* Show first page if not in window */}
        {pagination.page > 3 && (
          <>
            <Pagination.Item onClick={() => handlePageChange(1)}>
              1
            </Pagination.Item>
            {pagination.page > 4 && <Pagination.Ellipsis disabled />}
          </>
        )}

        {/* Window of pages around current page */}
        {[...Array(pagination.totalPages)].map((_, i) => {
          const page = i + 1;
          if (page >= pagination.page - 2 && page <= pagination.page + 2) {
            return (
              <Pagination.Item
                key={page}
                active={page === pagination.page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Pagination.Item>
            );
          }
          return null;
        })}

        {/* Show last page if not in window */}
        {pagination.page < pagination.totalPages - 2 && (
          <>
            {pagination.page < pagination.totalPages - 3 && (
              <Pagination.Ellipsis disabled />
            )}
            <Pagination.Item
              onClick={() => handlePageChange(pagination.totalPages)}
            >
              {pagination.totalPages}
            </Pagination.Item>
          </>
        )}

        <Pagination.Next
          onClick={() =>
            handlePageChange(
              Math.min(pagination.page + 1, pagination.totalPages)
            )
          }
          disabled={pagination.page === pagination.totalPages}
        />
        <Pagination.Last
          onClick={() => handlePageChange(pagination.totalPages)}
          disabled={pagination.page === pagination.totalPages}
        />
      </Pagination>
    );
  };

  // 🔹 Table rows renderer
  const renderRows = () => {
    if (!labels.length) {
      return (
        <tr>
          <td colSpan="5" className="text-center py-4 text-muted">
            No labels found
          </td>
        </tr>
      );
    }

    return labels.map((item, index) => (
      <tr key={item._id || index}>
        <td className="text-nowrap">
          {(pagination.page - 1) * 10 + (index + 1)}
        </td>
        <td className="text-break">{item.key}</td>
        <td className="text-break">{item.value?.en || "-"}</td>
        <td className="text-break">{item.value?.hi || "-"}</td>
        <td>
          <div className="d-flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="warning"
              onClick={() => handleEdit(item._id)}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                setDeleteId(item._id);
                setShowDeleteModal(true);
              }}
            >
              Delete
            </Button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <Row className="mb-4">
      <Col xs="12">
        <Card className="h-100 w-100">
          <Card.Header>
            <div
              style={{ width: "100%" }}
              className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3"
            >
              <h4 className="card-title mb-2 mb-md-0">Label Manager</h4>

              <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-50 custom-half">
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Search Label..."
                  value={searchTerm}
                  onChange={(e) => {
                    setPagination((p) => ({ ...p, page: 1 }));
                    setSearchTerm(e.target.value);
                  }}
                  className="flex-grow-1"
                  style={{ minWidth: "200px" }}
                />
                <Button
                  variant="primary"
                  size="sm"
                  className="w-100 w-sm-auto"
                  onClick={() =>
                    setModal({
                      show: true,
                      editing: false,
                      id: null,
                      labelKey: "",
                      labelEn: "",
                      labelHi: "",
                    })
                  }
                >
                  <i className="fa fa-plus me-1"></i> Add Label
                </Button>
              </div>
            </div>
          </Card.Header>

          <Card.Body className="px-0">
            {error && (
              <Alert
                variant="danger"
                className="mx-3"
                dismissible
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}

            <div className="table-responsive p-3">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <p className="mt-2">Loading Labels...</p>
                </div>
              ) : (
                <div className="table-responsive p-3">
                  <table className="table table-striped table-bordered mb-0">
                    <thead className="light">
                      <tr>
                        <th className="text-nowrap">Sno.</th>
                        <th>Label Key</th>
                        <th>Label in English</th>
                        <th>Label in Hindi</th>
                        <th className="text-nowrap">Action</th>
                      </tr>
                    </thead>
                    <tbody>{renderRows()}</tbody>
                  </table>
                  {renderPagination()}
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Add/Edit Label Modal */}
      <Modal
        show={modal.show}
        onHide={() => setModal({ ...modal, show: false })}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modal.editing ? "Edit Label" : "Add Label"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Label Key</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Label Key"
                value={modal.labelKey}
                onChange={(e) =>
                  setModal({ ...modal, labelKey: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Label in English</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter English Label"
                value={modal.labelEn}
                onChange={(e) =>
                  setModal({ ...modal, labelEn: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Label in Hindi</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Hindi Label"
                value={modal.labelHi}
                onChange={(e) =>
                  setModal({ ...modal, labelHi: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end">
          <Button
            variant="secondary"
            onClick={() => setModal({ ...modal, show: false })}
          >
            Cancel
          </Button>
          <Button variant="primary" disabled={loading} onClick={handleSave}>
            {loading ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Are you sure you want to delete this label?</p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={loading}>
            {loading ? <Spinner size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

export default LabelManager;
